import type { DuckDbCellValue } from '@cotera/client/app/etc/duckdb';
import type { DuckDbSourceLayer } from '@cotera/client/app/etc/duckdb/layers';

/**
 * The vocabulary the preview grid and everything laid over it are written in.
 *
 * A leaf module on purpose: the grid needs the layer type to accept one, and a
 * layer needs the row type to produce values for it. With both sides importing
 * from here neither has to import the other, which is what stopped
 * `dataset.page` and `dataset-automations` importing each other in a circle.
 */

export type DatasetPreviewRowData = Record<string, DuckDbCellValue>;

export type DatasetPreviewRow = DatasetPreviewRowData & {
  __coteraRowId: number;
};

export type DatasetPreviewStatus = 'idle' | 'loading' | 'ready' | 'error';

/** How many rows a settled run hands back to a caller that cannot see the grid. */
export const DATASET_PREVIEW_QUERY_SAMPLE_ROWS = 10;

/**
 * What a preview query did, for a caller that is not the grid.
 *
 * The grid itself reads the watchables directly; this exists for callers that
 * only get one shot at the answer — an action returning a result to Coco, or a
 * test asserting on a run — and so deliberately carries a sample rather than
 * the whole page.
 */
export type DatasetPreviewQueryResult = {
  /** Total matching rows, or null when the source could not count them. */
  rowCount: number | null;
  columns: string[];
  sampleRows: DatasetPreviewRowData[];
  /** The failure message, or null when the query succeeded. */
  error: string | null;
};

/**
 * One thing laid over the dataset's own columns.
 *
 * Just `DuckDbSourceLayer` bound to the preview's row type — the mechanism is
 * generic and lives in `etc/duckdb/layers`, because nothing about laying data
 * over a parquet table in DuckDB-wasm is specific to datasets.
 */
export type DatasetPreviewLayer = DuckDbSourceLayer<DatasetPreviewRow>;
